import { Injectable } from '@angular/core';
import { ReviewApplySession } from '@common/sockets/review-apply-session';
import { ReviewApplyFileResult } from '@common/reviews/models/review-apply-file-result.model';
import { ReviewApplyPackageRequestBody } from '@common/reviews/models/review-apply-package-request-body.model';
import { ReviewApplyState } from '@common/reviews/models/review-apply-state.model';
import { ApiService } from '@portal-core/auth/services/api.service';
import { AuthService } from '@portal-core/auth/services/auth.service';
import { CollabApiService } from '@portal-core/collab/services/collab-api.service';
import { OnlineService } from '@portal-core/general/services/online.service';
import { BehaviorSubject } from 'rxjs';

/**
 * Owns the lifecycle of a single review package apply operation: issues the HTTP request that
 * queues the apply on collab-pull, then opens a socket connection to collab-workspace's
 * `review-apply-{reviewPackageId}` room to receive live progress, folding every event into
 * `applyState$` for the progress dialog to bind to.
 *
 * Unlike a workspace pull session (which lives for the lifetime of the workspace page),
 * a review apply is a single operation kicked off from the progress dialog, so the socket
 * session here is created on `startApply` and torn down once the apply reaches a terminal state.
 */
@Injectable({
  providedIn: 'root'
})
export class ReviewApplyProgressService {
  /**
   * Computes a single 0-100 progress percentage for an apply state, based on how many of the
   * package's files have reached a terminal per-file outcome so far.
   */
  static aggregatePercent(state: ReviewApplyState | null): number {
    if (!state) {
      return 0;
    }
    if (state.status === 'completed') {
      return 100;
    }
    if (state.totalFiles <= 0) {
      return 0;
    }
    return Math.min(100, (state.filesProcessed / state.totalFiles) * 100);
  }

  /** Current apply state, or null when nothing is in flight or recent. */
  applyState$: BehaviorSubject<ReviewApplyState | null> = new BehaviorSubject<ReviewApplyState | null>(null);

  private session: ReviewApplySession | null = null;

  constructor(
    private apiService: ApiService,
    private authService: AuthService,
    private collabApiService: CollabApiService,
    private onlineService: OnlineService
  ) { }

  /**
   * Triggers a review package apply. Connects to the review-apply socket room for the package
   * before issuing the HTTP request so progress/completion events can't be missed in the window
   * between the request being queued and the socket connecting. `optimistic` is display-only data
   * the caller already has locally (which files/project/branch to actually apply is resolved
   * server-side from the package's own records, not from this request).
   */
  public startApply(body: ReviewApplyPackageRequestBody, optimistic: { branchName: string; licenseId: number; projectId: number; totalFiles: number }) {
    this.connectSession(body.reviewPackageId);

    // Optimistically reflect "running" so the dialog's progress bar is determinate from frame zero.
    this.applyState$.next({
      reviewPackageId: body.reviewPackageId,
      projectId: optimistic.projectId,
      branchName: optimistic.branchName,
      licenseId: optimistic.licenseId,
      option: body.option,
      status: 'running',
      totalFiles: optimistic.totalFiles,
      filesProcessed: 0,
      fileOutcomes: {},
      startedAt: new Date().toISOString(),
      startedByUserId: body.userId,
      startedByClientId: body.clientId,
      completedAt: null,
      result: null,
      error: null
    });

    this.collabApiService.applyPackageChanges$(body).subscribe({
      next: response => {
        // Two response shapes share one envelope:
        //  - status: 'running' (202) — the apply was queued on collab-pull. The terminal result
        //    will arrive via the `review.apply.completed`/`review.apply.failed` socket events;
        //    this HTTP body is informational only. Optimistic state is already in place.
        //  - status: 'completed' (200) — there were no files to apply, so the server short-
        //    circuited. No socket event will fire for this case, so drive terminal state now.
        const state = response?.Result?.applyState;
        if (state?.status === 'completed' || state?.status === 'failed') {
          this.applyState$.next(state);
          this.disconnectSession();
        }
      },
      error: error => {
        // 409 means another client already has an apply running for this review package. The
        // server returns the in-flight state so we can bind to it and observe its progress.
        const conflictState = this.extractConflictApplyState(error);
        if (conflictState) {
          this.applyState$.next(conflictState);
          if (conflictState.status !== 'running') {
            this.disconnectSession();
          }
          return;
        }

        this.applyState$.next({
          ...this.applyState$.value,
          status: 'failed',
          completedAt: new Date().toISOString(),
          error: { message: 'Unable to apply the review package changes.' }
        });
        this.disconnectSession();
      }
    });
  }

  /** Clears any leftover terminal state (e.g. after the dialog closes) so a fresh dialog open starts clean. */
  public clear() {
    this.disconnectSession();
    this.applyState$.next(null);
  }

  private extractConflictApplyState(error: unknown): ReviewApplyState | null {
    const httpError = error as { status?: number; error?: { Result?: { applyState?: ReviewApplyState } } };
    if (httpError?.status !== 409) {
      return null;
    }
    return httpError?.error?.Result?.applyState ?? null;
  }

  private connectSession(reviewPackageId: number) {
    this.disconnectSession();

    this.session = new ReviewApplySession({
      socketBaseUrl: this.apiService.collabWorkspaceUrl,
      offline: !this.onlineService.online,
      refreshToken$: () => this.authService.refreshToken$(),
      reviewPackageId
    });

    this.session.on('review.apply.started', (state: ReviewApplyState) => {
      this.applyState$.next(state);
    });

    this.session.on('review.apply.progress', (state: ReviewApplyState) => {
      this.applyState$.next(state);
    });

    this.session.on('review.apply.completed', (state: ReviewApplyState) => {
      this.applyState$.next(state);
      this.disconnectSession();
    });

    this.session.on('review.apply.failed', (state: ReviewApplyState) => {
      this.applyState$.next(state);
      this.disconnectSession();
    });

    // Per-file completion delta. Folds the outcome into the current state's `fileOutcomes` map
    // without waiting for the next aggregate-progress event to arrive.
    this.session.on('review.apply.file-completed', (data: { reviewFileId: number; fileOutcome: ReviewApplyFileResult }) => {
      const current = this.applyState$.value;
      if (!current) {
        return;
      }
      this.applyState$.next({
        ...current,
        fileOutcomes: { ...current.fileOutcomes, [data.reviewFileId]: data.fileOutcome }
      });
    });

    this.session.start();
  }

  private disconnectSession() {
    if (this.session) {
      this.session.stop();
      this.session = null;
    }
  }
}
